import { Injectable } from '@angular/core';
import {FFmpeg} from '@ffmpeg/ffmpeg';
import {fetchFile, toBlobURL} from '@ffmpeg/util';
import {FxDapHttpClient} from '../fx-http-client';
import {FFmpegConf} from './ffmpeg-conf';

@Injectable({
  providedIn: 'root'
})
export class FxFfmpegUtilService {
  ACCEPT_INPUT_FILE_TYPE = 'video/*';
  // BASE_URL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/esm';
  BASE_URL = 'assets/ffmpeg/esm/';

  ffmpeg: FFmpeg = new FFmpeg();
  message: string = '';
  transcodeProgress: Number = -1;

  videoElement: HTMLVideoElement;
  inputVideoFile: File;
  public destVideFile;
  data: any;
  private options: FFmpegConf;

  private handleLog: Function;
  private handleProgress: Function;

  constructor(
    protected http: FxDapHttpClient
  ) {
    // this.ffmpeg = new FFmpeg();
    this.message = '首先请选择本地视频文件';
  }

  public checkFileTypeSupport(type: string): boolean {
    if (this.ACCEPT_INPUT_FILE_TYPE === 'video/*') {
      return type.startsWith('video/');
    } else {
      return (this.ACCEPT_INPUT_FILE_TYPE.includes(type));
    }
  }

  public setInputVideoFile(file: File): void {
    this.inputVideoFile = file;

    this.message = '已加载原始视频，准备转码';
    this.transcodeProgress = -1;
  }

  public setFFmpegOptions(options: FFmpegConf): void {
    this.options = options;
  }

  public setLogHandler(handler: Function): void {
    this.handleLog = handler;
  }

  public setProgressHandler(handler: Function): void {
    this.handleProgress = handler;
  }

  private _callLogHandler(msg: string): void {
    if (typeof this.handleLog === 'function') {
      this.handleLog(msg);
    }
  }

  private _callProgressHandler({progress, time}): void {
    if (typeof this.handleProgress === 'function') {
      this.handleProgress({progress, time});
    }
  }

  public transcode(file?: File): Promise<void> {
    if (!this.inputVideoFile) {
      if (!file) {
        throw Error('首先请选择本地视频文件');
      }
      this.setInputVideoFile(file);
    }
    return this._transcode();
  }

  async _transcode() {
    this.message = '初始化 ffmpeg wasm';
    this._callLogHandler(this.message);

    try {
      // 注册 ffmpeg log & progress 事件监听
      if (!this.ffmpeg.loaded) {
        this.ffmpeg.on('log', ({ message: msg }) => {
          this.message = msg;
          this._callLogHandler(msg);
        });

        this.ffmpeg.on('progress', ({ progress, time}) => {
          this.transcodeProgress = parseFloat((progress * 100).toFixed(3));
          if (progress === 1) {
            setTimeout(() => {
              this.transcodeProgress = -1;
            }, 2500);
          }
          this._callProgressHandler({ progress, time});
        });
      }

      // 初始化 ffmpeg 对象
      if (!this.ffmpeg.loaded) {
        await this.ffmpeg.load({
          coreURL: await toBlobURL(`${this.BASE_URL}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${this.BASE_URL}/ffmpeg-core.wasm`, 'application/wasm'),
          classWorkerURL: await toBlobURL(`${this.BASE_URL}/ffmpeg-core.worker.js`, 'text/javascript'),
          workerURL: await toBlobURL(`${this.BASE_URL}/ffmpeg-core.worker.js`, 'text/javascript')
        });
      }

      this.message = '开始视频压缩';
      this._callLogHandler(this.message);

      await this.ffmpeg.writeFile('test.mp4', await fetchFile(this.inputVideoFile));
      const _options: string[] = this.options
        ? this.options.getCmd('test.mp4', 'dest.mp4')
        : ['-i', 'test.mp4',  '-crf', '28', '-preset', 'ultrafast', '-r', '24', '-b:a', '128k', 'dest.mp4'];
      await this.ffmpeg.exec(_options);
      this.message = '视频压缩完成';
      this._callLogHandler(this.message);

      this.data = await this.ffmpeg.readFile('dest.mp4');
      const originFileSize = (this.inputVideoFile.size / 1024 / 1024).toFixed(2);
      const destFileSize = (this.data.length / 1024 / 1024).toFixed(2);
      this.message = `视频压缩完成,
            原视频文件大小: ${originFileSize}MB
            压缩后文件大小: ${destFileSize}MB`;
      this._callLogHandler(this.message);

      this.destVideFile = URL.createObjectURL(new Blob([(this.data as any).buffer], {type: 'video/mp4'}));
      setTimeout(() => {
        this.transcodeProgress = -1;
      }, 500);
    } catch (err) {
      this.transcodeProgress = -1;
      console.log(err);
      // if (err.name === 'AbortError') {
      if (err.toString().includes('called FFmpeg.terminate()')) {
        this._callLogHandler('已终止视频压缩');
      } else {
        this._callLogHandler(err.toString());
      }
    }
  }

  public abort(): Promise<void> {
    return this._abort();
  }

  async _abort() {
    try {
      await this.ffmpeg.terminate();
    } catch (err) {
      console.error(err);
    } finally {
      this.inputVideoFile = null;
      this.data = null;
    }
  }

  public downloadVideo(): Promise<void> {
    return this._downloadVideo();
  }

  async _downloadVideo() {
    const a = document.createElement('a');
    a.style.cssText = 'display: none';
    a.href = this.destVideFile;
    a.download = `${this.inputVideoFile.name}-new.mp4`;
    document.body.appendChild(a);
    a.click();
  }

  public uploadVideo(): Promise<void> {
    return this._uploadVideo();
  }

  async _uploadVideo() {
    const formData = new FormData();
    // const blob: Blob = new Blob([(this.data as any).buffer], {type: 'video/mp4'});
    formData.append('attach', new File(
      // [blob],
      [(this.data as any).buffer],
      this.inputVideoFile.name,
      {type: 'video/mp4'}
    ));
    try {
      const response = await this.http.post('AI/IBaiduAIService/uploadFile', formData).toPromise();
      if (response.successValue && response.data) {
        const data = response.data;
        return data;
      } else {
        return null;
      }
    } catch (e) {
      console.error(e);
      return null;
    }
  }
}
